import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { IAuthResponse } from '@g99-product-web-ui/shared-interface-lib';
import { LOCAL_STORAGE_KEYS } from '@g99-product-web-ui/shared-util-lib';

import { LocalStorageService } from '../../services/helper-service/localStorage.service';
import { SubjectManageService } from '../../services/helper-service/subject-manager.service';
import { ToastService } from '../../services/toast-service/toast.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(
    private readonly _localStorageService: LocalStorageService,
    private readonly _toastService: ToastService,
    private readonly _authService: SubjectManageService
  ) {}

  /**
   * Determines if a route can be activated based on user authentication status.
   * If the session is valid, access is granted; otherwise, the user is redirected.
   *
   * @param route - Contains information about the route being accessed.
   * @param state - Represents the current state of the router, including the requested URL.
   * @returns {boolean | UrlTree} - `true` if access is allowed, otherwise redirects to login.
   */
  public canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree {
    if (this._isSessionValid()) {
      return true; // User session is valid, allow navigation
    }

    this._handleUnauthorized(state);
    return false;
  }

  /**
   * Validates the session by checking token existence and expiration time.
   * Starts an auto-logout timer if the session is valid.
   * @returns {boolean} - Returns true if the session is valid, otherwise false.
   */
  private _isSessionValid(): boolean {
    // Retrieve the current user's authentication details from local storage
    const currentUser = this._localStorageService.readItem(
      LOCAL_STORAGE_KEYS.CURRENT_USER
    ) as IAuthResponse;
    const token = currentUser?.idToken || null;
    const expirationTime = this._localStorageService.readItem<string>(
      LOCAL_STORAGE_KEYS.TOKEN_EXPIRATION
    );

    // If there's no token or expiration time, session is invalid
    if (!token || !expirationTime) {
      return false;
    }

    const expTime = Number.parseInt(expirationTime, 10); // Convert expiration time to a number
    if (Number.isNaN(expTime) || Date.now() >= expTime) {
      return false; // Token is either invalid or already expired
    }

    // Start auto-logout timer for the remaining session duration
    this._authService.startAutoLogoutTimer(expTime);
    return true;
  }

  /**
   * Handles session expiration by logging out the user and redirecting.
   */
  private _handleUnauthorized(state: RouterStateSnapshot): void {
    this._toastService.showError('Session expired. Please log in again!!!!');
    this._localStorageService.clearStorage();
    const returnUrl = state.url;
    this._localStorageService.storeItem(
      LOCAL_STORAGE_KEYS.RETURN_URL,
      returnUrl
    );
    // Pass true to preserve returnUrl during logout
    this._authService.logout(true);
  }
}
